/**
 * 并行刷新会员等级（getUserMemberInfo）与用户信息（getUserInfo），写入本地缓存，
 * 并发 member-info-updated / user-login（saveUser），避免会员变更后依赖旧缓存。
 */
import { AUTH_RELOGIN_MESSAGE, getToken } from '@/utils/request'
import { getUserInfo, getUserMemberInfo } from '@/api/user'
import { getUser, saveUser, authBus, resolveUserMobile } from '@/utils/auth'

function isBizSuccess(res) {
  return res && (res.code === 0 || res.code === '0')
}

/** 进行中的刷新 Promise，并发调用合并为一次接口请求（在请求结束后清空） */
let inflightRefresh = null

async function fetchMemberInfoSafe() {
  try {
    return await getUserMemberInfo()
  } catch (e) {
    if (e?.message === AUTH_RELOGIN_MESSAGE) throw e
    return null
  }
}

async function fetchUserInfoSafe() {
  try {
    return await getUserInfo()
  } catch (e) {
    if (e?.message === AUTH_RELOGIN_MESSAGE) throw e
    return null
  }
}

async function doRefresh() {
  const [memberRes, userRes] = await Promise.all([
    fetchMemberInfoSafe(),
    fetchUserInfoSafe(),
  ])

  const memberData = isBizSuccess(memberRes) && memberRes.data ? memberRes.data : null
  const userData = isBizSuccess(userRes) && userRes.data ? userRes.data : null

  const prev = getUser()
  if (!prev) {
    if (memberData) {
      authBus.$emit('member-info-updated', memberData)
    }
    return { memberData, userData }
  }

  let next = { ...prev }
  if (userData) {
    next = { ...next, ...userData }
    const phone = resolveUserMobile({ ...userData, phone: prev.phone })
    if (phone) next.phone = phone
    else delete next.phone
  }

  if (memberData) {
    const memberPatch = { ...(next.member || {}) }
    if (memberData.vipLevel != null && memberData.vipLevel !== '') {
      const id = Number(memberData.vipLevel)
      if (!Number.isNaN(id)) memberPatch.currentLevelId = id
    }
    if (memberData.cloudDisk != null && memberData.cloudDisk !== '') {
      memberPatch.cloudDisk = memberData.cloudDisk
    }
    next.member = memberPatch
  }

  if (memberData || userData) {
    saveUser(next)
  }

  if (memberData) {
    authBus.$emit('member-info-updated', memberData)
  }

  return { memberData, userData }
}

/**
 * 刷新用户会员/用户信息；同一次会话内并发调用复用同一个进行中的请求，避免重复发起。
 * @returns {Promise<{ memberData: object|null, userData: object|null }>}
 */
export async function refreshUserMemberAndProfile() {
  if (!getToken()) {
    return { memberData: null, userData: null }
  }
  if (inflightRefresh) return inflightRefresh
  inflightRefresh = doRefresh().finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}
